import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Box, Typography, CircularProgress, Paper, Button } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentCancelSkeleton from '../components/skeletons/PaymentCancelSkeleton';
import type { Order } from '../store/slices/orderSlice';
import { trackPageView, trackPaymentEvent, trackWithUserContext, getRestaurantId } from '../utils/analytics';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

interface OrderWithTable extends Order {
  table?: {
    qrCode?: string;
  };
}

const PaymentCancel: React.FC = () => {
  const [order, setOrder] = useState<OrderWithTable | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const tableId = localStorage.getItem('tableId');

  // Helper to get query param
  const getQueryParam = (name: string) => {
    return new URLSearchParams(location.search).get(name);
  };

  useEffect(() => {
    const orderId = getQueryParam('orderId');
    const errorMessage = getQueryParam('error');

    if (errorMessage) {
      setError(decodeURIComponent(errorMessage));
      setLoading(false);
      
      // Track payment error with enhanced context  
      const storedRestaurantId = localStorage.getItem('current_restaurant_id') || undefined;
      trackPageView(window.location.href, `Payment Error`, storedRestaurantId);
      trackWithUserContext(
        'payment_error',
        'payment_flow',
        'error_page',
        undefined,
        {
          error_message: decodeURIComponent(errorMessage),
          order_id: orderId,
          error_source: 'payment_provider'
        },
        currentUser,
        localStorage.getItem('tableId') || undefined,
        storedRestaurantId
      );
      
      return;
    }

    if (!orderId) {
      setError('Falta el ID de la orden');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const updateOrderStatus = async () => {
      try {
        await api.post(`/orders/${orderId}/payment-status`, { status: 'cancelled' });
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
        
        // Extract restaurant ID
        const restaurantId = getRestaurantId(response.data);
        
        // Track payment cancellation with enhanced context
        trackPageView(window.location.href, `Payment Cancelled - Order ${orderId}`, restaurantId);
        trackWithUserContext(
          'payment_cancelled_confirmed',
          'payment_flow',
          orderId || 'unknown',
          response.data.total,
          {
            order_id: orderId,
            cancellation_confirmed: true,
            order_total: response.data.total
          },
          currentUser,
          tableId || undefined,
          restaurantId
        );
        
      } catch (err: any) {
        setError(err.response?.data?.message || 'No se pudo actualizar el estado de la orden.');
      } finally {
        setLoading(false);
      }
    };

    updateOrderStatus();
  }, [location.search]);

  if (loading) {
    return <PaymentCancelSkeleton />;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      width: '100vw', 
      background: 'linear-gradient(135deg, #f5f5f5 0%, #ffebee 100%)', 
      py: 4 
    }}>
      <Box maxWidth="sm" sx={{ mx: 'auto', pb: { xs: 10, sm: 4 } }}>
        <Box sx={{ 
          mt: 4, 
          mb: 3, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <CancelIcon sx={{ 
            fontSize: 80, 
            color: '#f44336',
            animation: 'scaleIn 0.5s ease-out'
          }} />
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: '#d32f2f' }}>
            Pago Cancelado
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {error || 'Tu pago fue cancelado. Puedes intentar de nuevo o regresar al menú.'}
          </Typography>
        </Box>
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          background: 'white',
          border: '1px solid #e0e0e0',
          textAlign: 'center'
        }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => order?.table?.qrCode ? navigate(`/table/qr/${order.table.qrCode}`) : navigate('/')}
            sx={{ mt: 2 }}
          >
            Regresar al Menú
          </Button>
        </Paper>
      </Box>
      <style>
        {`
          @keyframes scaleIn {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
};

export default PaymentCancel; 